import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiGet, type GetResponse } from './client';
import { queryKeys } from './queryKeys';

export function useWorks() {
  return useQuery({
    queryKey: queryKeys.works,
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/works'>>('/api/v1/works', undefined, signal),
  });
}

export function workQueryOptions(workId: string) {
  return queryOptions({
    queryKey: queryKeys.work(workId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/works/{work_id}'>>(
        `/api/v1/works/${workId}`,
        undefined,
        signal,
      ),
    enabled: Boolean(workId),
  });
}

export function useWork(workId: string) {
  return useQuery(workQueryOptions(workId));
}

export function useWorkSummary(workId: string) {
  return useQuery({
    queryKey: queryKeys.summary(workId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/summary/{work_id}'>>(
        `/api/v1/summary/${workId}`,
        undefined,
        signal,
      ),
    enabled: Boolean(workId),
  });
}

export function useTimeline(workId: string) {
  // WorkLayout already loads this timeline: share its request and cached result.
  return useQuery({
    ...workQueryOptions(workId),
    select: (data) => data.timeline,
  });
}

export function graphQueryOptions(workId: string) {
  return queryOptions({
    queryKey: queryKeys.graph(workId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/works/{work_id}/graph'>>(
        `/api/v1/works/${workId}/graph`,
        undefined,
        signal,
      ),
    enabled: Boolean(workId),
  });
}

export function useGraph(workId: string) {
  return useQuery(graphQueryOptions(workId));
}

export function useProvisions(versionId: string, offset = 0, limit = 100) {
  return useQuery({
    staleTime: 10 * 60_000,
    queryKey: queryKeys.provisions(versionId, offset, limit),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/versions/{version_id}/provisions'>>(
        `/api/v1/versions/${versionId}/provisions`,
        { offset, limit },
        signal,
      ),
    enabled: Boolean(versionId),
  });
}

export function diffQueryOptions(
  fromVersion: string,
  toVersion: string,
  includeUnchanged = false,
) {
  return queryOptions({
    staleTime: 10 * 60_000,
    queryKey: queryKeys.diff(fromVersion, toVersion, includeUnchanged),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/diff'>>(
        '/api/v1/diff',
        {
          from_version: fromVersion,
          to_version: toVersion,
          include_unchanged: includeUnchanged,
        },
        signal,
      ),
    enabled: Boolean(fromVersion && toVersion),
  });
}

export function useDiff(
  fromVersion: string,
  toVersion: string,
  includeUnchanged = false,
) {
  return useQuery(diffQueryOptions(fromVersion, toVersion, includeUnchanged));
}

export function blameQueryOptions(workId: string, versionId: string) {
  return queryOptions({
    staleTime: 10 * 60_000,
    queryKey: queryKeys.blame(workId, versionId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/blame/{work_id}'>>(
        `/api/v1/blame/${workId}`,
        { version_id: versionId },
        signal,
      ),
    enabled: Boolean(workId && versionId),
  });
}

export function useBlame(workId: string, versionId: string) {
  return useQuery(blameQueryOptions(workId, versionId));
}

export function useChangeEvidence(changeId: string) {
  return useQuery({
    staleTime: 10 * 60_000,
    queryKey: queryKeys.evidence(changeId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/changes/{change_id}'>>(
        `/api/v1/changes/${encodeURIComponent(changeId)}`,
        undefined,
        signal,
      ),
    enabled: Boolean(changeId),
  });
}

export function useClassifications(
  workId: string,
  changeClass?: string,
  offset = 0,
  limit = 50,
) {
  return useQuery({
    queryKey: queryKeys.classifications(workId, changeClass, offset, limit),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/classifications/{work_id}'>>(
        `/api/v1/classifications/${workId}`,
        { change_class: changeClass, offset, limit },
        signal,
      ),
    enabled: Boolean(workId),
  });
}

export function useSearch(query: string, mode = 'latest', workId?: string, k = 10) {
  return useQuery({
    queryKey: queryKeys.search(query, mode, workId, k),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/search'>>(
        '/api/v1/search',
        { q: query, mode, work_id: workId, k },
        signal,
      ),
    enabled: Boolean(query && query.trim().length >= 2),
  });
}

export function useStats() {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/stats'>>('/api/v1/stats', undefined, signal),
  });
}

export function useMetrics() {
  return useQuery({
    queryKey: queryKeys.metrics,
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/metrics'>>('/api/v1/metrics', undefined, signal),
  });
}

export function useCoverage(workId?: string) {
  return useQuery({
    queryKey: queryKeys.coverage(workId),
    queryFn: ({ signal }) =>
      apiGet<GetResponse<'/api/v1/coverage'>>(
        '/api/v1/coverage',
        { work_id: workId },
        signal,
      ),
  });
}

/** Load every batch so the reader does not silently truncate a large Act. */
export function completeProvisionsQueryOptions(versionId: string) {
  return queryOptions({
    staleTime: 10 * 60_000,
    queryKey: [...queryKeys.provisions(versionId, 0, 500), 'complete'],
    enabled: Boolean(versionId),
    queryFn: async ({ signal }) => {
      const first = await apiGet<GetResponse<'/api/v1/versions/{version_id}/provisions'>>(
        `/api/v1/versions/${versionId}/provisions`,
        { offset: 0, limit: 500 },
        signal,
      );
      const provisions = [...first.provisions];
      while (provisions.length < first.total) {
        const next = await apiGet<
          GetResponse<'/api/v1/versions/{version_id}/provisions'>
        >(
          `/api/v1/versions/${versionId}/provisions`,
          { offset: provisions.length, limit: 500 },
          signal,
        );
        if (!next.provisions.length)
          throw new Error('The complete version could not be loaded. Please retry.');
        provisions.push(...next.provisions);
      }
      return { ...first, provisions };
    },
  });
}

export function useCompleteProvisions(versionId: string) {
  return useQuery(completeProvisionsQueryOptions(versionId));
}
